import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  getClusters,
  getClusterDetail,
  getClusterUtilization,
  getClusterCost,
  getClusterFilters,
  getClustersCostTrends,
} from "@/helpers/apis";
import type {
  ClustersParams,
  ClusterDetailParams,
  ClusterTimeseriesParams,
  ClustersCostTrendsParams,
  ClusterFilterParams,
} from "../types";

export const useClusters = (params: ClustersParams = {}, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-clusters",
      params.workspace_id,
      params.state,
      params.search,
      params.days,
      params.start_date,
      params.end_date,
      params.page,
      params.page_size,
      params.cluster_names,
      params.workspaces,
      params.compute_type,
      params.enable_photon,
      params.sortAttribute,
      params.sortOrder,
    ],
    queryFn: () => getClusters(params),
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useClusterDetail = (params: ClusterDetailParams, enabled = true) => {
  return useQuery({
    queryKey: ["databricks-cluster-detail", params.cluster_id],
    queryFn: () => getClusterDetail(params),
    enabled: enabled && Boolean(params.cluster_id),
  });
};

export const useClusterUtilization = (params: ClusterTimeseriesParams, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-cluster-utilization",
      params.cluster_id,
      params.start_date,
      params.end_date,
      params.granularity,
    ],
    queryFn: () => getClusterUtilization(params),
    enabled: enabled && Boolean(params.cluster_id),
  });
};

export const useClusterCost = (params: ClusterTimeseriesParams, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-cluster-cost",
      params.cluster_id,
      params.start_date,
      params.end_date,
      params.granularity,
    ],
    queryFn: () => getClusterCost(params),
    enabled: enabled && Boolean(params.cluster_id),
  });
};

export const useClusterFilters = (params: ClusterFilterParams = {}, enabled = true) => {
  return useQuery({
    queryKey: ["databricks-cluster-filters", params.workspace_id, params.prefix],
    queryFn: () => getClusterFilters(params),
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

export const useClustersCostTrends = (params: ClustersCostTrendsParams, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-clusters-cost-trends",
      params.start_date,
      params.end_date,
      params.granularity,
      params.workspace_id,
    ],
    queryFn: () => getClustersCostTrends(params),
    enabled,
  });
};
